import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { catchError, defer, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { ICartingComponentSortStrategy, ICartingConfig, ICartingGroupSortStrategy, ICartingSortOrder } from '../../../masterdata/carting/configuration/types';
import { CrudService } from '../crud.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataPropertySchema, IFileDataSchema, IFileDataSchemaProperties, IImportedData } from '../file-transfer-services/interfaces';
import { FailedImport, FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Configuration';
const fileName = 'Carting-configuration';
const configId = 'configuration';

const ORDER_LABELS = ['Ascending', 'Descending'] as const;
type OrderLabel = (typeof ORDER_LABELS)[number];

export interface ICartingConfigExportData {
	maxProductionSequenceNumberJumpsInSameWagon?: number;
	splitDoorAndWindowSashes: boolean;
	groupByColors: boolean;
	groupByProductionRoute?: boolean;
	groupByPaintInside?: boolean;
	groupByProfileSet?: boolean;
	groupByProfileSetGroup?: boolean;
	groupByGeometry?: boolean;
	groupSortByProductionSequenceNumber: boolean;
	groupSortByProductionSequenceNumberOrder?: string;
	groupSortByProductionSequenceNumberPriority?: number;
	componentSortByFrameField: boolean;
	componentSortByFrameFieldOrder?: string;
	componentSortByFrameFieldPriority?: number;
	componentSortByHeight: boolean;
	componentSortByHeightOrder?: string;
	componentSortByHeightPriority?: number;
	componentSortByWidth: boolean;
	componentSortByWidthOrder?: string;
	componentSortByWidthPriority?: number;
	componentSortBySurfaceArea: boolean;
	componentSortBySurfaceAreaOrder?: string;
	componentSortBySurfaceAreaPriority?: number;
}

const HEADER_WIDTH_PADDING = 2;

/** Every column at least as wide as its own header text. These fileKeys are long and verbose by design (e.g. "ComponentSortBySurfaceAreaPriority"), so a hand-picked width drifts out of sync the moment a header is renamed - deriving it from the header itself can't drift. */
function withHeaderWidths<T>(schema: IFileDataSchema<T>): IFileDataSchema<T> {
	const properties = Object.fromEntries(
		Object.entries(schema.properties).map(([key, property]) => {
			const prop = property as IFileDataPropertySchema<unknown>;
			const headerText = prop.fileKey ?? key;
			return [key, { ...prop, width: Math.max(prop.width ?? 0, headerText.length + HEADER_WIDTH_PADDING) }];
		}),
	) as IFileDataSchemaProperties<T>;

	return { ...schema, properties };
}

const rawCartingConfigSchema: IFileDataSchema<ICartingConfigExportData> = {
	name: sheetName,
	properties: {
		maxProductionSequenceNumberJumpsInSameWagon: { type: 'number', required: false, fileKey: 'MaxProductionSequenceNumberJumpsInSameWagon' },
		splitDoorAndWindowSashes: { type: 'boolean', required: true, fileKey: 'SplitDoorAndWindowSashes' },
		groupByColors: { type: 'boolean', required: true, fileKey: 'GroupByColors' },
		groupByProductionRoute: { type: 'boolean', required: false, fileKey: 'GroupByProductionRoute' },
		groupByPaintInside: { type: 'boolean', required: false, fileKey: 'GroupByPaintInside' },
		groupByProfileSet: { type: 'boolean', required: false, fileKey: 'GroupByProfileSet' },
		groupByProfileSetGroup: { type: 'boolean', required: false, fileKey: 'GroupByProfileSetGroup' },
		groupByGeometry: { type: 'boolean', required: false, fileKey: 'GroupByGeometry' },
		groupSortByProductionSequenceNumber: { type: 'boolean', required: true, fileKey: 'GroupSortByProductionSequenceNumber' },
		groupSortByProductionSequenceNumberOrder: { type: 'string', required: false, fileKey: 'GroupSortByProductionSequenceNumberOrder' },
		groupSortByProductionSequenceNumberPriority: { type: 'number', required: false, fileKey: 'GroupSortByProductionSequenceNumberPriority' },
		componentSortByFrameField: { type: 'boolean', required: true, fileKey: 'ComponentSortByFrameField' },
		componentSortByFrameFieldOrder: { type: 'string', required: false, fileKey: 'ComponentSortByFrameFieldOrder' },
		componentSortByFrameFieldPriority: { type: 'number', required: false, fileKey: 'ComponentSortByFrameFieldPriority' },
		componentSortByHeight: { type: 'boolean', required: true, fileKey: 'ComponentSortByHeight' },
		componentSortByHeightOrder: { type: 'string', required: false, fileKey: 'ComponentSortByHeightOrder' },
		componentSortByHeightPriority: { type: 'number', required: false, fileKey: 'ComponentSortByHeightPriority' },
		componentSortByWidth: { type: 'boolean', required: true, fileKey: 'ComponentSortByWidth' },
		componentSortByWidthOrder: { type: 'string', required: false, fileKey: 'ComponentSortByWidthOrder' },
		componentSortByWidthPriority: { type: 'number', required: false, fileKey: 'ComponentSortByWidthPriority' },
		componentSortBySurfaceArea: { type: 'boolean', required: true, fileKey: 'ComponentSortBySurfaceArea' },
		componentSortBySurfaceAreaOrder: { type: 'string', required: false, fileKey: 'ComponentSortBySurfaceAreaOrder' },
		componentSortBySurfaceAreaPriority: { type: 'number', required: false, fileKey: 'ComponentSortBySurfaceAreaPriority' },
	},
};

const CartingConfigSchema: IFileDataSchema<ICartingConfigExportData> = withHeaderWidths(rawCartingConfigSchema);

interface SortFieldDef<TSelf extends string> {
	enabledKey: keyof ICartingConfigExportData;
	orderKey: keyof ICartingConfigExportData;
	priorityKey: keyof ICartingConfigExportData;
	strategySelf: TSelf;
}

const GROUP_SORT_FIELDS: SortFieldDef<ICartingGroupSortStrategy>[] = [
	{
		enabledKey: 'groupSortByProductionSequenceNumber',
		orderKey: 'groupSortByProductionSequenceNumberOrder',
		priorityKey: 'groupSortByProductionSequenceNumberPriority',
		strategySelf: '/carting/config-group-sorting-strategies/productionSequenceNumber',
	},
];

const COMPONENT_SORT_FIELDS: SortFieldDef<ICartingComponentSortStrategy>[] = [
	{ enabledKey: 'componentSortByFrameField', orderKey: 'componentSortByFrameFieldOrder', priorityKey: 'componentSortByFrameFieldPriority', strategySelf: '/carting/config-component-sorting-strategies/frame-field' },
	{ enabledKey: 'componentSortByHeight', orderKey: 'componentSortByHeightOrder', priorityKey: 'componentSortByHeightPriority', strategySelf: '/carting/config-component-sorting-strategies/height' },
	{ enabledKey: 'componentSortByWidth', orderKey: 'componentSortByWidthOrder', priorityKey: 'componentSortByWidthPriority', strategySelf: '/carting/config-component-sorting-strategies/width' },
	{ enabledKey: 'componentSortBySurfaceArea', orderKey: 'componentSortBySurfaceAreaOrder', priorityKey: 'componentSortBySurfaceAreaPriority', strategySelf: '/carting/config-component-sorting-strategies/surface-area' },
];

function orderToLabel(order: ICartingSortOrder): OrderLabel {
	return order === 0 ? 'Ascending' : 'Descending';
}

function labelToOrder(label: OrderLabel): ICartingSortOrder {
	return label === 'Ascending' ? 0 : 1;
}

@Injectable({
	providedIn: 'root',
})
export class CartingConfigTransferService implements ITransferService<ICartingConfig> {
	private translationPrefix = 'masterdata.carting.configImportExport';

	constructor(
		private crudService: CrudService,
		private excelFileTransfer: ExcelFileTransfer<ICartingConfigExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// There is exactly one configuration per account, so - unlike rack/wagon - export always
	// carries a single row, and there is nothing to check for name/identity collisions.
	public exportData(configs: ICartingConfig[]): Observable<ICartingConfig[]> {
		const config = configs[0];
		if (!config) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.missing-configuration`)));
		}

		const row = this.toExportRow(config);

		// ExcelFileTransfer.exportData writes the file (and validates cell lengths) synchronously
		// before returning its observable, so calling it directly here would let a real write
		// failure throw past catchError below instead of reaching it - defer() delays that call
		// until subscription, inside RxJS's own error handling.
		return defer(() => this.excelFileTransfer.exportData(fileName, [row], CartingConfigSchema)).pipe(
			map(() => configs),
			catchError((error) => {
				throw new Error(this.translator.instant(`${this.translationPrefix}.export.errors.export-failed`, { error: error.message }));
			}),
		);
	}

	public downloadTemplate(): void {
		const exampleRow: ICartingConfigExportData = {
			maxProductionSequenceNumberJumpsInSameWagon: 10,
			splitDoorAndWindowSashes: true,
			groupByColors: true,
			groupByProductionRoute: true,
			groupByPaintInside: false,
			groupByProfileSet: false,
			groupByProfileSetGroup: false,
			groupByGeometry: false,
			groupSortByProductionSequenceNumber: true,
			groupSortByProductionSequenceNumberOrder: 'Ascending',
			groupSortByProductionSequenceNumberPriority: 1,
			componentSortByFrameField: true,
			componentSortByFrameFieldOrder: 'Ascending',
			componentSortByFrameFieldPriority: 2,
			componentSortByHeight: false,
			componentSortByWidth: false,
			componentSortBySurfaceArea: false,
		};

		this.excelFileTransfer.exportData(`${fileName}Template`, [exampleRow], CartingConfigSchema).subscribe();
	}

	private toExportRow(config: ICartingConfig): ICartingConfigExportData {
		const groupSorting = config.groupSorting ?? [];
		const componentSorting = config.componentSorting ?? [];
		const findSort = <T extends { type: { self: string } }>(list: T[], self: string) => list.find((entry) => entry.type.self === self);

		const groupSort = findSort(groupSorting, GROUP_SORT_FIELDS[0].strategySelf);
		const frameField = findSort(componentSorting, COMPONENT_SORT_FIELDS[0].strategySelf);
		const height = findSort(componentSorting, COMPONENT_SORT_FIELDS[1].strategySelf);
		const width = findSort(componentSorting, COMPONENT_SORT_FIELDS[2].strategySelf);
		const surfaceArea = findSort(componentSorting, COMPONENT_SORT_FIELDS[3].strategySelf);

		return {
			maxProductionSequenceNumberJumpsInSameWagon: config.maxProductionSequenceNumberJumpsInSameWagon ?? undefined,
			splitDoorAndWindowSashes: config.splitDoorAndWindowSashes ?? false,
			groupByColors: config.groupByColors ?? false,
			groupByProductionRoute: config.groupingCriterias?.productionRoute ?? undefined,
			groupByPaintInside: config.groupingCriterias?.paintInside ?? undefined,
			groupByProfileSet: config.groupingCriterias?.profileSet ?? undefined,
			groupByProfileSetGroup: config.groupingCriterias?.profileSetGroup ?? undefined,
			groupByGeometry: config.groupingCriterias?.geometry ?? undefined,
			groupSortByProductionSequenceNumber: !!groupSort,
			groupSortByProductionSequenceNumberOrder: groupSort ? orderToLabel(groupSort.order) : undefined,
			groupSortByProductionSequenceNumberPriority: groupSort?.priority ?? undefined,
			componentSortByFrameField: !!frameField,
			componentSortByFrameFieldOrder: frameField ? orderToLabel(frameField.order) : undefined,
			componentSortByFrameFieldPriority: frameField?.priority ?? undefined,
			componentSortByHeight: !!height,
			componentSortByHeightOrder: height ? orderToLabel(height.order) : undefined,
			componentSortByHeightPriority: height?.priority ?? undefined,
			componentSortByWidth: !!width,
			componentSortByWidthOrder: width ? orderToLabel(width.order) : undefined,
			componentSortByWidthPriority: width?.priority ?? undefined,
			componentSortBySurfaceArea: !!surfaceArea,
			componentSortBySurfaceAreaOrder: surfaceArea ? orderToLabel(surfaceArea.order) : undefined,
			componentSortBySurfaceAreaPriority: surfaceArea?.priority ?? undefined,
		};
	}

	// A single-row settings sheet: unlike rack/wagon there is nothing to batch, so the whole file
	// resolves to exactly one validation-or-save outcome instead of a per-row list.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((fileRowGroups) => {
				const parsedRows = fileRowGroups.flat();

				if (parsedRows.length === 0) {
					if (ImportErrors.length === 0) {
						ImportErrors.push({
							status: ImportStatus.ParsingError,
							message: this.translator.instant(`${this.translationPrefix}.import.errors.empty-file`),
						} as FailedParse);
					}
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				if (ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				if (parsedRows.length > 1) {
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.too-many-rows`),
					} as FailedParse);
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const { data: row, location } = parsedRows[0];
				const errors: Record<string, ValidationErrors> = {};

				this.validateRange(errors, 'maxProductionSequenceNumberJumpsInSameWagon', row.maxProductionSequenceNumberJumpsInSameWagon, 0, 1000);

				const groupSorting = this.parseSortParams(row, GROUP_SORT_FIELDS, errors);
				const componentSorting = this.parseSortParams(row, COMPONENT_SORT_FIELDS, errors);

				if (Object.keys(errors).length > 0) {
					ImportErrors.push({
						Id: configId,
						location,
						status: ImportStatus.ValidationError,
						errors,
					} as FailedValidationImport);
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				return this.crudService.get<{ items: ICartingConfig[] }>('/carting/configs').pipe(
					mergeMap((response) => {
						const existing = response.items[0];
						if (!existing) {
							ImportErrors.push({
								status: ImportStatus.ParsingError,
								message: this.translator.instant(`${this.translationPrefix}.import.errors.missing-configuration`),
							} as FailedParse);
							return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
						}

						const config = this.buildConfig(existing, row, groupSorting, componentSorting);

						return this.crudService.update<ICartingConfig>(config).pipe(
							map(
								() =>
									({
										successfulImports: [{ Id: configId, status: ImportStatus.SuccessUpdate } as SuccessFullImport],
										errors: ImportErrors,
									}) as ImportResponse,
							),
							catchError((error) => {
								ImportErrors.push({
									Id: configId,
									location,
									status: ImportStatus.Failed,
									message: this.translator.instant(`${this.translationPrefix}.import.errors.update-failed${this.saveFailedErrorKeySuffix(error)}`),
								} as FailedImport);
								return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
							}),
						);
					}),
					// Mirrors the update() catchError just above: without this, a failure fetching the
					// existing configuration (network drop, transient 5xx) would escape the
					// ImportResponse contract every other failure path in this function follows,
					// falling back to a generic, untranslated error instead of the itemized popup.
					catchError((error) => {
						console.error(error);
						ImportErrors.push({
							status: ImportStatus.ParsingError,
							message: this.translator.instant(`${this.translationPrefix}.import.errors.fetch-configuration-failed`),
						} as FailedParse);
						return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<Array<IImportedData<ICartingConfigExportData>>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, CartingConfigSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return of(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of([]);
			}
		};
	}

	// Uniqueness is scoped to the list passed in, mirroring the live form: the one group-sorting
	// control can never collide with itself, and the four component-sorting controls must only be
	// pairwise distinct among each other - never against group sorting's own priority.
	private parseSortParams<TSelf extends string>(row: ICartingConfigExportData, defs: SortFieldDef<TSelf>[], errors: Record<string, ValidationErrors>): Array<{ order: ICartingSortOrder; priority: number; type: { self: TSelf } }> {
		const seenPriorities = new Set<number>();
		const result: Array<{ order: ICartingSortOrder; priority: number; type: { self: TSelf } }> = [];

		for (const def of defs) {
			if (!row[def.enabledKey]) {
				continue;
			}

			const rawOrder = row[def.orderKey] as string | undefined;
			let order: ICartingSortOrder = 1;
			if (rawOrder !== undefined && String(rawOrder).trim() !== '') {
				const normalized = ORDER_LABELS.find((label) => label.toLowerCase() === String(rawOrder).trim().toLowerCase());
				if (!normalized) {
					errors[def.orderKey] = {
						invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-sort-order`, { value: rawOrder }),
					};
					continue;
				}
				order = labelToOrder(normalized);
			}

			const rawPriority = row[def.priorityKey] as number | undefined;
			let priority = 0;
			if (rawPriority !== undefined && rawPriority !== null) {
				// No lower bound, matching the live form's own priorityValidators (notUnique +
				// Validators.max only - see carting.config.view.model.ts): a negative priority saved
				// through some other path must still round-trip through export/import unchanged.
				if (!Number.isFinite(rawPriority) || !Number.isInteger(rawPriority) || rawPriority > 2147483647) {
					errors[def.priorityKey] = {
						range: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-priority`),
					};
					continue;
				}
				// The live form's own notUnique() treats a falsy control value - which includes 0 - as
				// exempt from the uniqueness check entirely (`if (!target.value) return null;`), since
				// an enabled-but-never-prioritized strategy is persisted as priority 0 by
				// getGroupSortingFromViewModel()/getComponentSortingFromViewModel()'s `Number(null)`.
				// Multiple strategies left at that default must import back cleanly, not collide.
				if (rawPriority !== 0) {
					if (seenPriorities.has(rawPriority)) {
						errors[def.priorityKey] = {
							duplicate: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-priority`),
						};
						continue;
					}
					seenPriorities.add(rawPriority);
				}
				priority = rawPriority;
			}

			result.push({ order, priority, type: { self: def.strategySelf } });
		}

		return result;
	}

	private validateRange(errors: Record<string, ValidationErrors>, field: string, value: number | undefined, min: number, max: number): void {
		if (value === undefined || value === null) {
			return;
		}

		if (!Number.isFinite(value) || !Number.isInteger(value) || value < min || value > max) {
			errors[field] = {
				range: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-max-jumps`, { min, max }),
			};
		}
	}

	private buildConfig(existing: ICartingConfig, row: ICartingConfigExportData, groupSorting: ICartingConfig['groupSorting'], componentSorting: ICartingConfig['componentSorting']): ICartingConfig {
		return {
			self: existing.self,
			maxProductionSequenceNumberJumpsInSameWagon: row.maxProductionSequenceNumberJumpsInSameWagon ?? null,
			splitDoorAndWindowSashes: row.splitDoorAndWindowSashes ?? false,
			groupByColors: row.groupByColors ?? false,
			groupingCriterias: {
				productionRoute: row.groupByProductionRoute ?? null,
				paintInside: row.groupByPaintInside ?? null,
				profileSet: row.groupByProfileSet ?? null,
				profileSetGroup: row.groupByProfileSetGroup ?? null,
				geometry: row.groupByGeometry ?? null,
			},
			groupSorting,
			componentSorting,
		};
	}

	private saveFailedErrorKeySuffix(error: unknown): string {
		const status = (error as HttpErrorResponse)?.status;
		if (status === 400) {
			return '-bad-request';
		}
		if (status === 404) {
			return '-not-found';
		}
		return '';
	}
}
